import {ref, computed, unref} from 'vue';
import {defineStore} from 'pinia';
import pinia from '@/znzs/new_src/store/index.js';
import {DIALOG_TYPE, DISPLAY_TYPE} from '@/znzs/new_src/constant/index';
import {pushMessage} from '@/znzs/src/utils/chat';
import {
    allProductTypes,
    ticketUserInfo,
    recommondProduct,
    getPermision,
} from '@/znzs/src/api/ticket';

/**
 * 获取用户在工单侧的权限
 * @returns
 */
function usePermission() {
    const permission = ref();
    /**
     * 获取用户在工单侧的权限
     */
    async function getTicketPermission() {
        try {
            const res = await getPermision();
            if (res?.permission) {
                permission.value = res.permission;
            }
        }
        catch {}
    }

    function checkPermission() {
        if (!unref(permission)) {
            pushMessage(
                DIALOG_TYPE.HTML,
                DISPLAY_TYPE.HTML,
                // bca-disable-next-line
                '抱歉，您没有工单权限，请联系账号管理员为您添加TicketUserControl或TicketFullControl权限策略。详情请参考文档：<a href="https://cloud.baidu.com/doc/IAM/s/yjxu3gfsl#%E5%B9%B3%E5%8F%B0%E6%A8%A1%E5%9D%97%E6%9D%83%E9%99%90">多用户访问控制-平台模块权限</a>'
            );
        }
        return !!unref(permission);
    }

    return {
        permission,
        checkPermission,
        getTicketPermission,
    };
}

function useProductTypes() {
    let queryProductTypesTask = null;
    let queryCommondProductTask = null;
    const productTypes = ref([]); // 完整的产品树
    const recommondProducts = ref([]); // 推荐产品列表
    const favorTypeIds = ref([]); // 常用产品，暂时没用

    // 只有二级数据
    const productTypeList = computed(() => unref(productTypes).flatMap(productType => productType.children));
    // 二级产品数据的id映射
    const productTypeMaps = computed(() => {
        return unref(productTypeList).reduce((map, productType) => {
            productType.name = productType.displayValue;
            map[String(productType.id)] = productType;
            return map;
        }, {});
    });
    // 二级产品数据的名称映射，暂时没有使用场景
    const productTypeNameMaps = computed(() => {
        return unref(productTypeList).reduce((map, productType) => {
            map[productType.serviceType] = productType;
            return map;
        }, {});
    });
    // 3级数据转换,feature改为children，方便tree渲染
    const productTree = computed(() => {
        const tree = unref(productTypes).map(({...category}) => {
            category.children = category.children.map(({...productType}) => {
                if (productType.feature && !productType.children) {
                    productType.children = productType.feature;
                }
                return productType;
            });
            return category;
        });
        return tree;
    });
    // 三级问题数据的平铺列表
    const questionTypeList = computed(() => {
        return unref(productTypeList).flatMap(questionMap => questionMap.feature);
    });
    // 三级问题数据的id映射
    const questionTypeMaps = computed(() => {
        return unref(questionTypeList).reduce((map, questionType) => {
            questionType.name = questionType.value;
            map[String(questionType.id)] = questionType;
            return map;
        }, {});
    });
    /**
     * 获取工单侧全部产品树
     * @returns
     */
    async function queryProductTypes() {
        try {
            if (!queryProductTypesTask) {
                queryProductTypesTask = allProductTypes();
            }
            const data = await queryProductTypesTask;
            productTypes.value = data;
            return data;
        }
        catch {}
    }
    /**
     * 获取推荐产品
     * @returns
     */
    async function queryRecommondProduct() {
        try {
            if (!queryCommondProductTask) {
                queryCommondProductTask = recommondProduct();
            }
            const data = await queryCommondProductTask;
            recommondProducts.value = data?.recommendTypes || [];
            favorTypeIds.value = data?.favorTypeIds || [];
            return data;
        }
        catch {}
    }

    /**
         * 根据产品ID，反查一级ID
         * @param {*} state
         * @returns
         */
    function getCategoryByProductId(productId) {
        if (!unref(productTree)) {
            return;
        };
        let category = null;
        for (const cate of unref(productTree)) {
            const product = cate.children
                .find(product => Number(product.id) === Number(productId));
            if (product) {
                category = cate;
                break;
            }
        }
        return category;
    };

    function setEllipsis({groupIndex, typeIndex, isEllipsis}) {
        const data = unref(productTypes);
        const item = data[groupIndex].children[typeIndex];
        item.isEllipsis = isEllipsis;
        data[groupIndex].children[typeIndex] = {...item};
        productTypes.value = [...data];
    }

    return {
        productTypes,
        productTree,
        productTypeList,
        recommondProducts,
        productTypeMaps,
        productTypeNameMaps,
        questionTypeList,
        questionTypeMaps,
        setEllipsis,
        queryProductTypes,
        queryRecommondProduct,
        getCategoryByProductId,
    };
}

function useTicketUser() {
    const ticketUserInfo = ref({}); // 工单系统用户信息

    let queryUserTask = null;
    async function queryTicketUser() {
        try {
            if (!queryUserTask) {
                queryUserTask = ticketUserInfo();
            }
            const data = await queryUserTask;
            ticketUserInfo.value = data;
            return data;
        }
        catch {}
    }
    return {
        ticketUserInfo,
        queryTicketUser,
    };
}

export const useTicketStore = defineStore('ticketStore', () => {

    const globalTicketDialog = ref(null);

    function setGlobalTicketDialog(dialog) {
        globalTicketDialog.value = dialog;
    }

    return {
        ...useProductTypes(),
        ...usePermission(),
        ...useTicketUser(),
        globalTicketDialog,
        setGlobalTicketDialog,
    };
});

export const useTicketStoreOut = () => useTicketStore(pinia);